/**
 * Custom Hooks for ERP Settings
 * Provides React hooks for managing ERP system-wide configuration
 */

import { useCallback } from 'react';
import { useResourceCollection, useResourceById, useCreateResource, useUpdateResource, useDeleteResource, ResourceState, MutationState } from './useResource';
import { erpSettingsService } from '../services';
import { ERPSetting } from '../types';
import { ResourceQueryOptions } from '../services/resources.service';

/**
 * Hook to fetch all ERP settings (typically returns a single settings record)
 */
export function useERPSettings(
  options?: ResourceQueryOptions
): ResourceState<ERPSetting> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      erpSettingsService.getAll(opts) as unknown as Promise<{ data: ERPSetting[]; meta?: any }>,
    []
  );
  return useResourceCollection<ERPSetting>(fetchFn, options);
}

/**
 * Hook to fetch ERP settings by ID
 */
export function useERPSetting(id: number | null, options?: ResourceQueryOptions) {
  return useResourceById<ERPSetting>((id) => erpSettingsService.getById(id, options), id);
}

/**
 * Hook to create ERP settings (usually only needed once)
 */
export function useCreateERPSettings(onSuccess?: () => void): MutationState<ERPSetting> {
  return useCreateResource<ERPSetting>(erpSettingsService.create, onSuccess);
}

/**
 * Hook to update ERP settings
 */
export function useUpdateERPSettings(onSuccess?: () => void): MutationState<ERPSetting> {
  return useUpdateResource<ERPSetting>(erpSettingsService.update, onSuccess);
}

/**
 * Hook to delete ERP settings
 */
export function useDeleteERPSettings(onSuccess?: () => void): MutationState<void> {
  return useDeleteResource(erpSettingsService.delete, onSuccess);
}
